import React, { useRef, useState } from 'react'
import PrivateValue from '../PrivateValue.jsx'
import { canRestoreSetup } from '../../lib/workspace/restoreSetup.js'

const price = value => value == null ? '—' : Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

function HistoricalSetupCard({ project, onRestore, onRestored }) {
  const [open, setOpen] = useState(false)
  const [restoring, setRestoring] = useState(false)
  const [restored, setRestored] = useState(false)
  const [error, setError] = useState(null)
  const restoreId = useRef(null)
  const pending = useRef(false)
  const sizing = Array.isArray(project.account_sizing) ? project.account_sizing : []
  const plannedShares = sizing.reduce((sum, row) => sum + (Number(row.shares) || 0), 0)
  const plannedRisk = sizing.reduce((sum, row) => sum + (Number(row.max_loss) || 0), 0)
  const restore = async () => {
    if (pending.current || restored || !onRestore) return
    pending.current = true
    setRestoring(true)
    setError(null)
    try {
      if (!restoreId.current) restoreId.current = crypto.randomUUID()
      await onRestore(project, restoreId.current)
      setRestored(true)
      setOpen(false)
      onRestored?.()
    } catch (cause) {
      setError(cause?.message || 'Could not restore this setup. Please retry.')
    } finally {
      pending.current = false
      setRestoring(false)
    }
  }
  return (
    <article className="rounded-lg border border-border bg-canvas font-mono text-[11px]">
      <button type="button" aria-expanded={open} onClick={() => setOpen(value => !value)} className="w-full rounded-lg p-3 text-left hover:bg-canvas2/40 focus-visible:outline focus-visible:outline-1 focus-visible:outline-inkMuted">
        <div className="flex justify-between gap-2">
          <span className="font-semibold text-ink">{project.setup_pattern || project.symbol}</span>
          <span className="capitalize text-inkMuted">{project.setup_status}</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-3">
          <span className="text-forest">{price(project.trigger_price)}</span>
          <span className="text-ember">{price(project.pattern_failure_price)}</span>
          <span className="text-blue-400">{price(project.sell_target_price)}</span>
        </div>
      </button>
      {open && (
        <div role="region" aria-label={`${project.setup_pattern || project.symbol} historical details`} className="border-t border-border p-3 space-y-3 text-xs leading-relaxed text-ink">
          <div className="text-ink">Saved setup details · Read only</div>
          <dl className="grid grid-cols-2 gap-2 [&>dt]:font-semibold">
            <dt>Symbol</dt><dd>{project.symbol}</dd>
            <dt>Entry</dt><dd>{project.entry_type || '—'}</dd>
            <dt>Trigger</dt><dd>{price(project.trigger_price)}</dd>
            <dt>Stop</dt><dd>{price(project.pattern_failure_price)}</dd>
            <dt>Target</dt><dd>{price(project.sell_target_price)}</dd>
            <dt>Size</dt><dd>{project.recommended_size_pct == null ? '—' : `${project.recommended_size_pct}%`}</dd>
            <dt>Planned shares</dt><dd><PrivateValue>{plannedShares}</PrivateValue></dd>
            <dt>Planned risk</dt><dd><PrivateValue>{price(plannedRisk)}</PrivateValue></dd>
            {project.created_at && <><dt>Created</dt><dd>{new Date(project.created_at).toLocaleString()}</dd></>}
            {project.closed_at && <><dt>Retired</dt><dd>{new Date(project.closed_at).toLocaleString()}</dd></>}
          </dl>
          {sizing.map((account, index) => (
            <div key={account.account_number || index}>{account.nick_name || account.account_name || `Account ending ${String(account.account_number || '').slice(-4)}`} · <PrivateValue>{account.shares ?? '—'}</PrivateValue> shares</div>
          ))}
          {project.setup_source && <div>Source: {project.setup_source}</div>}
          {project.setup_notes && <p className="whitespace-pre-wrap">{project.setup_notes}</p>}
          {Array.isArray(project.schwab_order_ids) && project.schwab_order_ids.length > 0 && <div>{project.schwab_order_ids.length} historical order references retained.</div>}
          {onRestore && <div className="space-y-2">
            <p>{canRestoreSetup(project) ? 'Restores these details as a new idea and moves this card out of Previous setups. No orders are sent.' : 'No saved setup details to restore.'}</p>
            <button type="button" disabled={restoring || restored || !canRestoreSetup(project)} onClick={restore} className="rounded border border-border px-3 py-2 text-ink hover:bg-canvas2 disabled:opacity-50">
              {restoring ? 'Restoring…' : restored ? 'Restored as new idea' : 'Restore as new idea'}
            </button>
          </div>}
          {error && <div role="alert" className="text-ember">{error}</div>}
        </div>
      )}
    </article>
  )
}

export default function SetupHistory({ projects = [], error = null, onRestore }) {
  const history = useRef(null)
  if (!projects.length && !error) return null
  return (
    <details ref={history} data-testid="setup-history" className="rounded-lg border border-border p-3 text-inkMuted">
      <summary className="cursor-pointer font-mono text-[11px]">Previous setups{!error && ` (${projects.length})`}</summary>
      {error && <p role="alert" className="mt-2 text-[11px]">{error}</p>}
      <div className="mt-3 space-y-3">
        {projects.map(project => <HistoricalSetupCard key={project.id} project={project} onRestore={onRestore} onRestored={() => { if (history.current) history.current.open = false }} />)}
      </div>
    </details>
  )
}
